import type { LevelThreeProgressItem, LevelTwoProgressItem } from '@lemonade-hq/uniclient-chat';
import { Flex, spacing } from '@lemonade-hq/uniclient-theme';
import { AnimatePresence, motion } from 'motion/react';
import { getAnimationState } from '../animationHelpers';
import { textVariants } from '../animations';
import { LEVEL_ONE_TO_TWO_GAP, TEXT_LINE_HEIGHT } from '../constants';
import type { AnimationProgressProps, BaseProgressItemProps, SubItemDotProps } from '../types';
import { ItemContainer } from './common/ItemContainer';
import { ItemContentWrapper } from './common/ItemContentWrapper';
import { ItemTitle } from './common/ItemTitle';
import { SubItemDot } from './Dots';
import { ProgressSubSubItemsWrapper } from './ProgressSubSubItemsWrapper';

export interface ProgressSubItemProps extends BaseProgressItemProps, SubItemDotProps {
  readonly item: LevelTwoProgressItem;
  readonly subItems?: LevelThreeProgressItem[];
  readonly currentSubSubItemIndex: AnimationProgressProps['currentSubSubItemIndex'];
  readonly isFirst: boolean;
  readonly isFirstRender: boolean;
}

export const ProgressSubItem: React.FC<ProgressSubItemProps> = ({
  isFirst,
  item,
  completed,
  current,
  currentSubSubItemIndex,
  previousSubItemHasSubSubItems,
  isFirstRender,
  isBackwards,
}) => {
  const showSubSubItems = current && !completed && item.subItems != null && currentSubSubItemIndex !== -1;
  const subTextVariants = textVariants({
    level: 2,
    isExpanding: isFirst,
    previousSubItemHasSubSubItems,
    isBackwards,
  });

  return (
    <ItemContainer style={{ marginBlockStart: isFirst ? LEVEL_ONE_TO_TWO_GAP : 0 }}>
      <Flex alignItems="flex-start" gap={spacing.s16}>
        <Flex
          flexDirection="column"
          height="1lh"
          justifyContent="center"
          style={{ lineHeight: `${TEXT_LINE_HEIGHT}em` }}
        >
          <SubItemDot
            completed={completed}
            current={current}
            isBackwards={isBackwards}
            isFirst={isFirst}
            previousSubItemHasSubSubItems={previousSubItemHasSubSubItems}
          />
        </Flex>
        <ItemContentWrapper isFirstRender={isFirstRender}>
          <motion.div
            animate={getAnimationState(completed, current)}
            initial={!isFirstRender}
            layout
            variants={subTextVariants}
          >
            <ItemTitle
              completed={completed}
              current={current}
              isBackwards={isBackwards}
              isExpanding={isFirst}
              level={2}
              title={item.title}
              type="bodySmall"
            />
          </motion.div>
          <AnimatePresence initial={false} mode="popLayout">
            {showSubSubItems && (
              <ProgressSubSubItemsWrapper
                currentSubSubItemIndex={currentSubSubItemIndex ?? -1}
                isBackwards={isBackwards}
                isFirstRender={isFirstRender}
                items={item.subItems}
              />
            )}
          </AnimatePresence>
        </ItemContentWrapper>
      </Flex>
    </ItemContainer>
  );
};
